import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ThemeSynthesis,
  VisionSynthesis,
  TarotReading,
} from '../types'
import {
  generateWrappedContent,
  type GenerateWrappedInput,
} from '../services/wrappedApi'
import { saveJourney } from '../services/api'

// AI-generated wrapped content
export interface WrappedHoroscope {
  cosmicTheme: string
  yearOverview: string
  keyEvents: Array<{
    event: string
    timing: string
    meaning: string
    action: string
  }>
  monthlyHighlights: Array<{
    month: string
    theme: string
    focus: string
  }>
  guidance: string[]
}

export interface WrappedPhotoAnalysis {
  photos: Array<{
    id: string
    theme: string
    emotionalTone: string
    story: string
    caption: string
  }>
  overallNarrative: string
  yearInImages: string
}

export interface WrappedNarrative {
  yearTheme: string
  celebrationMessage: string
  keyPatterns: Array<{
    pattern: string
    evidence: string[]
  }>
  growthAreas: Array<{
    area: string
    insight: string
    carryForward: string
  }>
  strengthsRevealed: string[]
  lookingAhead: {
    buildOn: string
    watchFor: string
    question: string
  }
}

export interface WrappedIntention {
  intentionId: string
  statement: string
  whyItMatters: string
  identityStatement: string
  implementation: {
    cue: string
    action: string
    twoMinuteVersion: string
  }
  celebrationIdeas: string[]
}

export interface WrappedTheme {
  mainTheme: string
  tagline: string
  summary: string
  keyStats: Array<{
    label: string
    value: string
  }>
}

// Connecting threads - ties everything together for the big reveal
export interface WrappedConnectingThreads {
  threads: Array<{
    thread: string
    evidence: string[]
  }>
  yearWord: string
  revealStatement: string
}

export interface WrappedData {
  horoscope: WrappedHoroscope | null
  photos: WrappedPhotoAnalysis | null
  narrative: WrappedNarrative | null
  intentions: WrappedIntention[]
  theme: WrappedTheme | null
  // New theme and vision synthesis from redesigned exercises
  themeSynthesis: ThemeSynthesis | null
  visionSynthesis: VisionSynthesis | null
  // Tarot card reading for the year
  tarot: TarotReading | null
  // Connecting threads - the big reveal
  connectingThreads: WrappedConnectingThreads | null
  generatedAt: string | null
}

interface WrappedStore {
  wrapped: WrappedData
  isGenerating: boolean
  error: string | null
  generateWrapped: (input: GenerateWrappedInput) => Promise<void>
  reset: () => void
}

const initialWrapped: WrappedData = {
  horoscope: null,
  photos: null,
  narrative: null,
  intentions: [],
  theme: null,
  themeSynthesis: null,
  visionSynthesis: null,
  tarot: null,
  connectingThreads: null,
  generatedAt: null,
}

export const useWrappedStore = create<WrappedStore>()(
  persist(
    (set) => ({
      wrapped: initialWrapped,
      isGenerating: false,
      error: null,

      generateWrapped: async (input: GenerateWrappedInput) => {
        set({ isGenerating: true, error: null })

        const result = await generateWrappedContent(input)

        if (!result.success) {
          set({
            isGenerating: false,
            error: result.error,
          })
          throw new Error(result.error)
        }

        const data = result.data
        const tarotReading: TarotReading = {
          cardIndex: data.tarot.cardIndex,
          isReversed: data.tarot.isReversed,
          interpretation: data.tarot.interpretation,
          advice: data.tarot.advice,
        }

        const wrappedData: WrappedData = {
          horoscope: data.horoscope,
          photos: data.photos,
          narrative: data.narrative,
          intentions: data.intentions,
          theme: data.theme,
          themeSynthesis: data.themeSynthesis,
          visionSynthesis: data.visionSynthesis,
          tarot: tarotReading,
          connectingThreads: data.connectingThreads,
          generatedAt: new Date().toISOString(),
        }

        set({
          wrapped: wrappedData,
          isGenerating: false,
        })

        // Save to database if user is logged in
        if (input.userId) {
          const year = new Date().getFullYear()
          saveJourney(input.userId, year, {
            wrappedData: wrappedData,
          }).catch(err => console.error('Failed to save wrapped to database:', err))
        }
      },

      reset: () => {
        set({
          wrapped: initialWrapped,
          isGenerating: false,
          error: null,
        })
      },
    }),
    {
      name: 'spark-wrapped-storage',
      partialize: (state) => ({
        wrapped: state.wrapped,
      }),
    }
  )
)
